import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, WritableSignal, computed, effect, signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { EMPTY, Observable, Subject, combineLatest, defer, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
  RateDashCurrency, RateDashDimension, RateDashFilters, RateDashGridState, RateDashRateCard,
  RequestRateDashDetail, RequestRateDashFilter, RequestRateDashFilterOptions, RequestRateDashSummary,
  ResponseRateDashDetail, ResponseRateDashFilterOptions, ResponseRateDashSummary
} from '../models';
import { RateDashboardApiService } from './rate-dashboard-api.service';
import { RateDashboardCacheService } from './rate-dashboard-cache.service';

/** How a multi-select dimension is labelled, requested and written to the URL. */
export interface RateDashDimensionDef {
  key: RateDashDimension;
  label: string;
  chipLabel: string;
  requestKey: keyof RequestRateDashFilter;
  urlKey: string;
  searchable: boolean;
}

/** The multi-select filters, in panel order. */
export const RATE_DASH_DIMENSIONS: RateDashDimensionDef[] = [
  { key: 'region', label: 'Region', chipLabel: 'Region', requestKey: 'regions', urlKey: 'region', searchable: false },
  { key: 'country', label: 'Country', chipLabel: 'Country', requestKey: 'countries', urlKey: 'country', searchable: true },
  { key: 'role', label: 'Role', chipLabel: 'Role', requestKey: 'roles', urlKey: 'role', searchable: true },
  { key: 'skillSet', label: 'Skill', chipLabel: 'Skill', requestKey: 'skillSets', urlKey: 'skill', searchable: true },
  { key: 'consultancyType', label: 'Consultancy type', chipLabel: 'Consultancy', requestKey: 'consultancyTypes', urlKey: 'ctype', searchable: false },
  { key: 'staffLocation', label: 'Staff location', chipLabel: 'Staff', requestKey: 'staffLocations', urlKey: 'loc', searchable: false },
  { key: 'confidenceLevel', label: 'Confidence', chipLabel: 'Confidence', requestKey: 'confidenceLevels', urlKey: 'conf', searchable: false }
];

/**
 * One removable chip above the content. dim 'yoe' is the experience range. A chip that
 * stands for several values (a whole region's countries) lists them in values.
 */
export interface RateDashChip {
  dim: RateDashDimension | 'yoe';
  label: string;
  value: string;
  values?: string[];
}

/** One value in a filter list. */
export interface RateDashOptionItem {
  value: string;
  rows: number;
  selected: boolean;
  /** A country's region; null for other filters, or before script 16 is run. */
  parent: string | null;
}

/** One experience band: yoeMin to yoeMax years, with its combinations (null when unknown). */
export interface RateDashYoeBand {
  min: number;
  max: number;
  rows: number | null;
}

/** A country the filters offer, by ISO3: its rate-card labels and region. */
export interface RateDashOfferedCountry {
  labels: string[];
  region: string | null;
}

/** One filter section for the panel. Only dimensions the rate card has are listed. */
export interface RateDashSection {
  def: RateDashDimensionDef;
  items: RateDashOptionItem[];
  selectedCount: number;
}

/** A rate card with the tiers it has. */
export interface RateDashCardChoice {
  rateId: string;
  name: string;
  tiers: string[];
}

const ctDebounceMs = 300;
const ctDefaultCardName = 'BAU Rates';
const ctDefaultTier = 'Tier 1';
const ctDefaultCurrency = 'USD';
const ctListSeparator = '|';

const DEFAULT_GRID: RateDashGridState = { page: 1, pageSize: 100, sortBy: 'P50', sortDesc: true };

function emptyFilters(): RateDashFilters {
  return {
    rateId: null, tierType: null, currency: ctDefaultCurrency,
    region: [], country: [], role: [], skillSet: [], consultancyType: [], staffLocation: [], confidenceLevel: [],
    yoeFrom: null, yoeTo: null
  };
}

/**
 * State for the IT Labor Rate Dashboard: the filters, the three responses and their
 * loading and error states.
 *
 * Provided by the dashboard page, not the root, so each visit starts from the URL.
 *
 * HOW A FILTER CHANGE FLOWS
 * The filters are one signal. Every change waits {@link ctDebounceMs} after the LAST
 * change before anything is requested, so ticking five boxes costs one round trip.
 * switchMap then drops - and so aborts - any request still in flight for the previous
 * filters; the API passes that abort to SQL Server. Options, summary and detail are three
 * independent streams so each shows as soon as it arrives, and summary and detail run in
 * parallel. Every response is cached per exact request for the session.
 *
 * The previous response stays in its signal while the next one loads. Components dim
 * it (see summaryPending / detailPending) rather than blanking the page.
 */
@Injectable()
export class RateDashboardStateService implements OnDestroy {

  //-------------------------  Inputs  -------------------------------------
  /** What the user has chosen. The single source of truth. */
  readonly filters = signal<RateDashFilters>(emptyFilters());
  /** Detail grid paging and sort. Kept apart so paging never refetches the charts. */
  readonly grid = signal<RateDashGridState>({ ...DEFAULT_GRID });

  //-------------------------  View state  ---------------------------------
  /** The percentile the map and ranking show. Client-side only: no request. */
  readonly metric = signal<'medianP10' | 'medianP50' | 'medianP90'>('medianP50');
  /** The country under the pointer, shared so the map and ranking highlight together. */
  readonly hoverIso = signal<string | null>(null);

  //-------------------------  Responses  ----------------------------------
  readonly options = signal<ResponseRateDashFilterOptions | null>(null);
  readonly summary = signal<ResponseRateDashSummary | null>(null);
  readonly detail = signal<ResponseRateDashDetail | null>(null);

  readonly optionsLoading = signal<boolean>(false);
  readonly summaryLoading = signal<boolean>(false);
  readonly detailLoading = signal<boolean>(false);

  readonly optionsError = signal<string | null>(null);
  readonly summaryError = signal<string | null>(null);
  readonly detailError = signal<string | null>(null);

  /** The filters each response was fetched for, so a pending change can be shown at once. */
  protected readonly summaryKey = signal<string | null>(null);
  protected readonly detailKey = signal<string | null>(null);

  //-------------------------  Derived  ------------------------------------
  /** A rate card and tier are chosen, so summary and detail can be requested. */
  readonly ready = computed(() => !!this.filters().rateId && !!this.filters().tierType);

  /** True from the moment a filter changes until the summary for it arrives. */
  readonly summaryPending = computed(() =>
    this.ready() && (this.summaryLoading() || this.summaryKey() !== this.keyOf(this.filters())));

  /** True from the moment a filter or page changes until the detail for it arrives. */
  readonly detailPending = computed(() =>
    this.ready() && (this.detailLoading() || this.detailKey() !== this.keyOf(this.filters(), this.grid())));

  /** The rate cards, one entry per card with its tiers, in the order the server lists them. */
  readonly cards = computed<RateDashCardChoice[]>(() => {
    const out: RateDashCardChoice[] = [];
    for (const c of this.options()?.rateCards ?? []) {
      if (!c.rateId || !c.rateName || !c.tierType)
        continue;
      let card = out.find(x => x.rateId === c.rateId);
      if (!card) {
        card = { rateId: c.rateId, name: c.rateName, tiers: [] };
        out.push(card);
      }
      if (!card.tiers.includes(c.tierType))
        card.tiers.push(c.tierType);
    }
    return out;
  });

  readonly currentCard = computed(() => this.cards().find(c => c.rateId === this.filters().rateId) ?? null);

  /** The loaded rate card and tier row - its generatedOn is "last refreshed". */
  readonly currentCardTier = computed<RateDashRateCard | null>(() =>
    (this.options()?.rateCards ?? []).find(c => c.rateId === this.filters().rateId && c.tierType === this.filters().tierType) ?? null);

  /** Currencies, USD first then by code. */
  readonly currencies = computed<RateDashCurrency[]>(() =>
    [...(this.options()?.currencies ?? [])].sort((a, b) =>
      a.currencyCode === ctDefaultCurrency ? -1 : b.currencyCode === ctDefaultCurrency ? 1
        : (a.currencyCode ?? '').localeCompare(b.currencyCode ?? '')));

  readonly currentCurrency = computed(() =>
    this.currencies().find(c => c.currencyCode === this.filters().currency) ?? null);

  /**
   * YoE slider bounds: the lowest band minimum to the highest band maximum, or null when
   * the rate card has no experience bands (Consulting).
   */
  readonly yoeBounds = computed<[number, number] | null>(() => {
    const r = this.options()?.yoeRange;
    const high = r?.yoeMaxHigh ?? r?.yoeMinHigh;
    return r?.yoeMinLow != null && high != null ? [r.yoeMinLow, high] : null;
  });

  /**
   * The experience bands that exist for the other filters, lowest first - the only stops
   * the Experience slider offers. They come from script 17 ('yoeBand' options); before it,
   * every band within the bounds, with no counts. Empty for Consulting.
   */
  readonly yoeBands = computed<RateDashYoeBand[]>(() => {
    if (!this.optionsMatchCard())
      return [];
    const listed = (this.options()?.options ?? [])
      .filter(o => o.dimension === 'yoeBand' && !!o.value)
      .map(o => {
        const [min, max] = o.value!.split('-').map(Number);
        return { min, max, rows: o.rows ?? 0 };
      })
      .filter(b => Number.isFinite(b.min) && Number.isFinite(b.max));
    if (listed.length)
      return listed.sort((a, b) => a.min - b.min || a.max - b.max);

    const bounds = this.yoeBounds();
    const out: RateDashYoeBand[] = [];
    if (bounds)
      for (let min = bounds[0]; min <= bounds[1]; min++)
        for (let max = min; max <= bounds[1]; max++)
          out.push({ min, max, rows: null });
    return out;
  });

  /**
   * The filters each rate card and tier ("rateId|tier") has shown this visit. A filter the
   * other filters have emptied keeps its button; one the card lacks never gets one.
   */
  protected readonly knownDims = signal<Map<string, Set<RateDashDimension>>>(new Map());

  /**
   * The card and tier ("rateId|tier") the current options were requested for. The first
   * call, before a card is chosen, lists every card's filters; until options for the
   * chosen card arrive the filter bar shows placeholders rather than the wrong buttons.
   */
  protected readonly optionsFor = signal<string | null>(null);

  /** Whether the current options belong to the chosen card and tier. */
  readonly optionsMatchCard = computed(() => {
    const f = this.filters();
    return !!f.rateId && this.optionsFor() === `${f.rateId}|${f.tierType}`;
  });

  /**
   * The filter sections the current rate card has. Dimensions it lacks are left out, not
   * shown empty - but one it has, emptied by the other filters, stays (with no values).
   */
  readonly sections = computed<RateDashSection[]>(() => {
    const f = this.filters();
    if (!this.optionsMatchCard())
      return [];
    const all = this.options()?.options ?? [];
    const known = this.knownDims().get(`${f.rateId}|${f.tierType}`);
    const out: RateDashSection[] = [];

    for (const def of RATE_DASH_DIMENSIONS) {
      const chosen = f[def.key];
      const items: RateDashOptionItem[] = all
        .filter(o => o.dimension === def.key && !!o.value)
        .map(o => ({ value: o.value!, rows: o.rows ?? 0, selected: chosen.includes(o.value!), parent: o.parent ?? null }));

      // A chosen value the other filters have ruled out still needs a box to untick.
      for (const v of chosen)
        if (!items.some(i => i.value === v))
          items.push({ value: v, rows: 0, selected: true, parent: null });

      if (items.length || known?.has(def.key))
        out.push({ def, items, selectedCount: chosen.length });
    }
    return out;
  });

  /**
   * The countries the Geography options offer, by ISO3, with their rate-card labels and
   * region. Those options ignore the country filter, so the map can still add a country
   * outside the current selection, and zoom to a whole region.
   */
  readonly offeredCountries = computed<Map<string, RateDashOfferedCountry>>(() => {
    const out = new Map<string, RateDashOfferedCountry>();
    for (const o of this.options()?.options ?? [])
      if (o.dimension === 'country' && o.code && o.value) {
        const c = out.get(o.code) ?? { labels: [], region: o.parent ?? null };
        c.labels.push(o.value);
        out.set(o.code, c);
      }
    return out;
  });

  /**
   * Countries offered per region, from the Geography options. Empty until script 16 is
   * run, when options carry no region.
   */
  readonly regionCountries = computed<Map<string, string[]>>(() => {
    const out = new Map<string, string[]>();
    for (const i of this.sections().find(s => s.def.key === 'country')?.items ?? [])
      if (i.parent)
        out.set(i.parent, [...(out.get(i.parent) ?? []), i.value]);
    return out;
  });

  /**
   * Active filters as removable chips, in the order they were chosen within each filter.
   * A region whose countries are all chosen shows as one chip, "EMEA (all)".
   */
  readonly chips = computed<RateDashChip[]>(() => {
    const f = this.filters();
    const out: RateDashChip[] = [];
    const whole = new Map<string, string[]>();
    for (const [region, countries] of this.regionCountries())
      if (countries.length > 1 && countries.every(c => f.country.includes(c)))
        whole.set(region, countries);
    const covered = new Set([...whole.values()].flat());

    for (const def of RATE_DASH_DIMENSIONS) {
      if (def.key === 'country')
        for (const [region, countries] of whole)
          out.push({ dim: 'country', label: 'Region', value: `${region} (all)`, values: countries });
      for (const v of f[def.key])
        if (def.key !== 'country' || !covered.has(v))
          out.push({ dim: def.key, label: def.chipLabel, value: v });
    }
    if (f.yoeFrom != null || f.yoeTo != null)
      out.push({ dim: 'yoe', label: 'YoE', value: `${f.yoeFrom ?? this.yoeBounds()?.[0] ?? ''}–${f.yoeTo ?? this.yoeBounds()?.[1] ?? ''} yrs` });
    return out;
  });

  //-------------------------  Plumbing  -----------------------------------
  protected readonly retry$ = new Subject<void>();
  protected readonly destroy$ = new Subject<void>();

  constructor(protected api: RateDashboardApiService
    , protected cache: RateDashboardCacheService
    , protected router: Router
    , protected route: ActivatedRoute) {

    this.filters.set(this.fromQuery(this.route.snapshot.queryParamMap));
    this.grid.set(this.gridFromQuery(this.route.snapshot.queryParamMap));

    const filters$ = toObservable(this.filters).pipe(
      debounceTime(ctDebounceMs),
      distinctUntilChanged((a, b) => this.keyOf(a) === this.keyOf(b)));

    const withGrid$ = combineLatest([toObservable(this.filters), toObservable(this.grid)]).pipe(
      debounceTime(ctDebounceMs),
      distinctUntilChanged((a, b) => this.keyOf(a[0], a[1]) === this.keyOf(b[0], b[1])));

    const retryFilters$ = this.retry$.pipe(map(() => this.filters()));
    const retryWithGrid$ = this.retry$.pipe(map(() => [this.filters(), this.grid()] as [RateDashFilters, RateDashGridState]));

    // Options: also the first call, with no rate card, to list the cards.
    merge(filters$, retryFilters$).pipe(
      switchMap(f => this.load$('options', this.optionsRequest(f), r => this.api.filterOptionsGet$(r),
        this.optionsLoading, this.optionsError).pipe(map(resp => ({ resp, f })))),
      takeUntil(this.destroy$)
    ).subscribe(({ resp, f }) => {
      this.options.set(resp);
      this.optionsFor.set(f.rateId && f.tierType ? `${f.rateId}|${f.tierType}` : null);
      this.learnDims(f, resp);
      if (!this.filters().rateId)
        this.applyDefaultCard(resp);
    });

    // Summary
    merge(filters$, retryFilters$).pipe(
      filter(f => !!f.rateId && !!f.tierType),
      switchMap(f => this.load$('summary', this.summaryRequest(f), r => this.api.summaryGet$(r),
        this.summaryLoading, this.summaryError).pipe(map(resp => ({ resp, key: this.keyOf(f) })))),
      takeUntil(this.destroy$)
    ).subscribe(({ resp, key }) => {
      this.summary.set(resp);
      this.summaryKey.set(key);
    });

    // Detail
    merge(withGrid$, retryWithGrid$).pipe(
      filter(([f]) => !!f.rateId && !!f.tierType),
      switchMap(([f, g]) => this.load$('detail', this.detailRequest(f, g), r => this.api.detailGet$(r),
        this.detailLoading, this.detailError).pipe(map(resp => ({ resp, key: this.keyOf(f, g) })))),
      takeUntil(this.destroy$)
    ).subscribe(({ resp, key }) => {
      this.detail.set(resp);
      this.detailKey.set(key);
    });

    // URL: written whenever the choice changes, replacing history so Back leaves the page.
    effect(() => {
      const f = this.filters();
      const g = this.grid();
      if (!f.rateId)
        return;
      const queryParams = this.toQuery(f, g);
      untracked(() => this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true }));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //-------------------------  Commands  -----------------------------------

  /** Switches rate card. Filter values differ between cards, so they are cleared. */
  setRateCard(rateId: string): void {
    const card = this.cards().find(c => c.rateId === rateId);
    if (!card || rateId === this.filters().rateId)
      return;
    const tier = card.tiers.includes(this.filters().tierType ?? '') ? this.filters().tierType : card.tiers[0] ?? null;
    this.filters.set({ ...emptyFilters(), rateId, tierType: tier, currency: this.filters().currency });
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  setTier(tierType: string): void {
    if (!this.currentCard()?.tiers.includes(tierType) || tierType === this.filters().tierType)
      return;
    this.filters.update(f => ({ ...f, tierType }));
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  setCurrency(currency: string): void {
    if (currency && currency !== this.filters().currency)
      this.filters.update(f => ({ ...f, currency }));
  }

  /** Ticks or unticks one value. */
  toggleValue(dim: RateDashDimension, value: string): void {
    this.filters.update(f => {
      const list = f[dim].includes(value) ? f[dim].filter(v => v !== value) : [...f[dim], value];
      return { ...f, [dim]: list };
    });
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  /**
   * Sets one dimension's values outright - the map uses this for a plain click, which
   * replaces the country selection, against toggleValue for shift-click.
   */
  setValues(dim: RateDashDimension, values: string[]): void {
    this.filters.update(f => ({ ...f, [dim]: [...new Set(values)] }));
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  /**
   * Sets the experience band: From is the band's minimum and To its maximum, so 6-6 picks
   * the 6-6 band only. Every band can be chosen, the widest included; clearYoe removes it.
   */
  setYoe(from: number, to: number): void {
    this.filters.update(f => ({ ...f, yoeFrom: from, yoeTo: to }));
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  clearYoe(): void {
    this.filters.update(f => ({ ...f, yoeFrom: null, yoeTo: null }));
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  removeChip(chip: RateDashChip): void {
    if (chip.dim === 'yoe')
      this.clearYoe();
    else if (chip.values) {
      const drop = new Set(chip.values);
      this.setValues(chip.dim, this.filters()[chip.dim].filter(v => !drop.has(v)));
    }
    else
      this.toggleValue(chip.dim, chip.value);
  }

  /** Clears every filter but keeps the rate card, tier and currency. */
  clearAll(): void {
    const f = this.filters();
    this.filters.set({ ...emptyFilters(), rateId: f.rateId, tierType: f.tierType, currency: f.currency });
    this.grid.update(g => ({ ...g, page: 1 }));
  }

  setPage(page: number, pageSize?: number): void {
    this.grid.update(g => ({ ...g, page: Math.max(1, page), pageSize: pageSize ?? g.pageSize }));
  }

  setSort(sortBy: string, sortDesc: boolean): void {
    this.grid.update(g => ({ ...g, sortBy, sortDesc, page: 1 }));
  }

  /** Re-requests whatever failed, for the current filters. */
  retry(): void {
    this.retry$.next();
  }

  /**
   * Remembers which filters a card and tier has, from options requested for it - the first
   * call, for no card, lists every card's filters and is not used (see sections).
   */
  protected learnDims(f: RateDashFilters, resp: ResponseRateDashFilterOptions | null): void {
    if (!f.rateId || !f.tierType)
      return;
    const key = `${f.rateId}|${f.tierType}`;
    const dims = new Set(this.knownDims().get(key) ?? []);
    const before = dims.size;
    for (const o of resp?.options ?? [])
      if (RATE_DASH_DIMENSIONS.some(d => d.key === o.dimension))
        dims.add(o.dimension as RateDashDimension);
    if (dims.size !== before)
      this.knownDims.update(m => new Map(m).set(key, dims));
  }

  //-------------------------  Requests  -----------------------------------

  protected applyFilters<T extends RequestRateDashFilter>(req: T, f: RateDashFilters): T {
    req.rateId = f.rateId;
    req.tierType = f.tierType;
    // Sorted so the same choice made in a different order hits the same cache entry.
    for (const def of RATE_DASH_DIMENSIONS)
      (req as any)[def.requestKey] = f[def.key].length ? [...f[def.key]].sort() : null;
    // An exact band: yoeMin = From and yoeMax = To.
    req.yoeMinFrom = req.yoeMinTo = f.yoeFrom;
    req.yoeMaxFrom = req.yoeMaxTo = f.yoeTo;
    return req;
  }

  protected optionsRequest(f: RateDashFilters): RequestRateDashFilterOptions {
    return this.applyFilters(new RequestRateDashFilterOptions(), f);
  }

  protected summaryRequest(f: RateDashFilters): RequestRateDashSummary {
    const req = this.applyFilters(new RequestRateDashSummary(), f);
    req.displayCurrency = f.currency;
    return req;
  }

  protected detailRequest(f: RateDashFilters, g: RateDashGridState): RequestRateDashDetail {
    const req = this.applyFilters(new RequestRateDashDetail(), f);
    req.displayCurrency = f.currency;
    req.page = g.page;
    req.pagesize = g.pageSize;
    req.sortBy = g.sortBy;
    req.sortDesc = g.sortDesc;
    return req;
  }

  /**
   * One cached, cancellable load. The loading flag is set inside defer, i.e. when the
   * request is actually subscribed - AFTER switchMap has unsubscribed the previous one,
   * whose finalize would otherwise clear the flag for the request that replaced it.
   */
  protected load$<Req, Resp>(kind: string, req: Req, call: (r: Req) => Observable<Resp>
    , loading: WritableSignal<boolean>, error: WritableSignal<string | null>): Observable<Resp> {

    const key = `${kind}:${JSON.stringify(req)}`;
    const hit = this.cache.get<Resp>(key);
    if (hit) {
      error.set(null);
      return of(hit);
    }

    return defer(() => {
      loading.set(true);
      error.set(null);
      return call(req);
    }).pipe(
      tap(resp => {
        // getData$ answers {} when there is no token; never cache that.
        if (resp && (resp as any).success)
          this.cache.set(key, resp);
      }),
      catchError((err: unknown) => {
        error.set(this.errorText(err));
        return EMPTY;
      }),
      finalize(() => loading.set(false))
    );
  }

  protected errorText(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0)
        return 'The server could not be reached. Check your connection and try again.';
      if (err.status === 400 && typeof err.error === 'string')
        return err.error;
      return `The server returned an error (${err.status}).`;
    }
    return 'Something went wrong loading the rates.';
  }

  /** On first load with no card in the URL: BAU Rates, Tier 1, else the first card listed. */
  protected applyDefaultCard(resp: ResponseRateDashFilterOptions): void {
    const cards = resp.rateCards ?? [];
    const pick = cards.find(c => c.rateName === ctDefaultCardName && c.tierType === ctDefaultTier) ?? cards[0];
    if (pick?.rateId)
      this.filters.update(f => ({ ...f, rateId: pick.rateId!, tierType: pick.tierType ?? null }));
  }

  //-------------------------  Keys and URL  -------------------------------

  /** A stable key for a choice; lists are sorted so order does not matter. */
  protected keyOf(f: RateDashFilters, g?: RateDashGridState): string {
    const norm: any = { ...f };
    for (const def of RATE_DASH_DIMENSIONS)
      norm[def.key] = [...f[def.key]].sort();
    return JSON.stringify(g ? [norm, g] : norm);
  }

  protected toQuery(f: RateDashFilters, g: RateDashGridState): Params {
    const q: Params = { card: f.rateId, tier: f.tierType };
    if (f.currency !== ctDefaultCurrency)
      q['cur'] = f.currency;
    for (const def of RATE_DASH_DIMENSIONS)
      if (f[def.key].length)
        q[def.urlKey] = f[def.key].join(ctListSeparator);
    if (f.yoeFrom != null || f.yoeTo != null)
      q['yoe'] = `${f.yoeFrom ?? ''}-${f.yoeTo ?? ''}`;
    if (g.page !== DEFAULT_GRID.page)
      q['page'] = g.page;
    if (g.pageSize !== DEFAULT_GRID.pageSize)
      q['size'] = g.pageSize;
    if (g.sortBy !== DEFAULT_GRID.sortBy || g.sortDesc !== DEFAULT_GRID.sortDesc)
      q['sort'] = `${g.sortBy}${g.sortDesc ? ':desc' : ''}`;
    return q;
  }

  protected fromQuery(p: ParamMap): RateDashFilters {
    const f = emptyFilters();
    f.rateId = p.get('card');
    f.tierType = f.rateId ? p.get('tier') : null;
    f.currency = (p.get('cur') ?? ctDefaultCurrency).toUpperCase();
    for (const def of RATE_DASH_DIMENSIONS) {
      const raw = p.get(def.urlKey);
      f[def.key] = raw ? [...new Set(raw.split(ctListSeparator).map(v => v.trim()).filter(v => !!v))] : [];
    }
    const yoe = /^(\d*(?:\.\d+)?)-(\d*(?:\.\d+)?)$/.exec(p.get('yoe') ?? '');
    if (yoe) {
      f.yoeFrom = yoe[1] ? Number(yoe[1]) : null;
      f.yoeTo = yoe[2] ? Number(yoe[2]) : null;
    }
    return f;
  }

  protected gridFromQuery(p: ParamMap): RateDashGridState {
    const g = { ...DEFAULT_GRID };
    const page = Number(p.get('page'));
    const size = Number(p.get('size'));
    if (page >= 1)
      g.page = Math.floor(page);
    if (size >= 1 && size <= 1000)
      g.pageSize = Math.floor(size);
    const sort = p.get('sort');
    if (sort) {
      const [by, dir] = sort.split(':');
      g.sortBy = by;
      g.sortDesc = dir === 'desc';
    }
    return g;
  }
}
